import {
  ApplicationConfig,
  inject,
  provideAppInitializer,
  provideBrowserGlobalErrorListeners,
  provideZoneChangeDetection,
} from '@angular/core';
import { provideRouter, withComponentInputBinding } from '@angular/router';
import { routes } from './app.routes';
import { provideAnimationsAsync } from '@angular/platform-browser/animations/async';
import { provideHttpClient, withInterceptors } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';
import { credentialsInterceptor } from './interceptors/credentials.interceptor';
import {
  AuthHttpService,
  AuthService,
  sesionExpiradaInterceptor,
} from '@auth';
import { proveerI18n, proveerOverlays } from '@intelishs-group/ui';

export const appConfig: ApplicationConfig = {
  providers: [
    // ⚠️ Desde Angular 21 una app SIN este provider arranca zoneless. La migracion
    // de 20 → 21 lo agrega para conservar el comportamiento con zone.js que la
    // app tenia; quitarlo es pasar a zoneless, y eso es un cambio aparte que hay
    // que probar pantalla por pantalla, no un efecto de subir de version.
    provideZoneChangeDetection(),

    // `withComponentInputBinding` conecta los parametros de ruta, el `data` y
    // los `resolve` con los `input()` del componente cuyo NOMBRE coincide. Es lo
    // que hace que `/pedidos/:folio` llene el `folio` del detalle y que el
    // rotulo de un modulo pendiente entre por `data`.
    //
    // ⚠️ Sin esta opcion los `input()` se quedan sin valor y NO hay error: el
    // detalle abre con el folio vacio y la pantalla de "en desarrollo" sin
    // titulo. Es la falla silenciosa de la que avisa `app.routes.ts`.
    provideRouter(routes, withComponentInputBinding()),
    provideBrowserGlobalErrorListeners(),

    // El orden importa: `credentialsInterceptor` pone `withCredentials` en cada
    // peticion —la sesion viaja en cookies `HttpOnly` y la app NUNCA ve un
    // token—, y `sesionExpiradaInterceptor` refresca y reintenta UNA vez ante un
    // 401. Sin el segundo, la sesion muere a media pantalla cada vez que expira
    // el access token.
    provideHttpClient(withInterceptors([credentialsInterceptor, sesionExpiradaInterceptor])),

    provideAnimationsAsync(),
    // Los overlays del CDK vuelven al apilamiento por z-index (ui >= 5.2.7). Sin
    // esto, desde Angular 21 un modal abierto tapa los avisos de SweetAlert y el
    // velo de carga: salen detras, no se pueden pulsar y la pantalla parece colgada.
    proveerOverlays(),
    // Idioma de la plataforma (ui >= 5.2.10): registra `es-MX`, fija `LOCALE_ID`
    // y le pasa el mismo locale a ng-zorro, que NO usa el `LOCALE_ID` de Angular
    // para sus date pickers. Las tres piezas salen de un solo valor.
    proveerI18n(),

    // Guardas, shell y pantallas dependen solo de la clase abstracta; la
    // implementacion se elige aqui, con UN provider. Solo hay una: la que habla
    // con el backend. El login simulado se retiro para que ningun ambiente
    // pueda entrar sin pasar por `BrokersPortal.API`.
    { provide: AuthService, useClass: AuthHttpService },

    // Rehidrata la sesion ANTES del primer render. Sin esto, un F5 dentro de la
    // app pinta el login por un instante y el parpadeo parece un cierre de
    // sesion espurio.
    provideAppInitializer(() => firstValueFrom(inject(AuthService).restaurarSesion())),
  ],
};
